type InviteRouteMatch = { id: string; loaderData?: unknown };
type InviteLocation = { pathname: string; search: string; hash: string };
const INVITE_SLUG_PATTERN = /^[A-Za-z0-9]{6,32}$/;

export function getPlayerFriendInviteSlug(
  location: Pick<InviteLocation, 'pathname' | 'search'>,
  matches: readonly InviteRouteMatch[]
): string | null {
  const slug = new URLSearchParams(location.search).get('friend_invite');
  return isPlayerFriendInviteRoute(location.pathname, matches) &&
    slug &&
    INVITE_SLUG_PATTERN.test(slug)
    ? slug
    : null;
}

export function isPlayerFriendInviteRoute(
  path: string,
  matches: readonly InviteRouteMatch[]
): boolean {
  const pathname = path.replace(/\/$/, '');
  return (
    pathname === '/daily' ||
    pathname === '/daily-challenge' ||
    pathname === '/endless-trivia' ||
    /^\/quiz\/[^/]+$/.test(pathname) ||
    /^\/partner\/[^/]+\/leaderboard$/.test(pathname) ||
    matches.some((match) => {
      const data = match.loaderData;
      return (
        match.id.endsWith('/$name') &&
        typeof data === 'object' &&
        data !== null &&
        'tagExists' in data &&
        data.tagExists === true
      );
    })
  );
}

export function withoutFriendInvite(location: InviteLocation): InviteLocation {
  const nextSearch = new URLSearchParams(location.search);
  nextSearch.delete('friend_invite');
  const search = nextSearch.toString();
  return { pathname: location.pathname, search: search ? `?${search}` : '', hash: location.hash };
}

export interface PendingFriendInvitation {
  slug: string;
  destinationPath: string;
  principalKey: string;
  skipped: boolean;
}
const PENDING_INVITE_KEY = 'atrivial:pending-friend-invitation';
const INVITE_CONTEXT_CHANGED = 'atrivial:friend-invitation-context';
let storageUnavailable = false;
let memoryContext: PendingFriendInvitation | null = null;
export function readPendingFriendInvitation(): PendingFriendInvitation | null {
  if (storageUnavailable) return memoryContext;
  try {
    const value = JSON.parse(sessionStorage.getItem(PENDING_INVITE_KEY) ?? 'null');
    if (
      value &&
      INVITE_SLUG_PATTERN.test(value.slug) &&
      typeof value.destinationPath === 'string' &&
      typeof value.principalKey === 'string' &&
      typeof value.skipped === 'boolean'
    )
      return value;
  } catch {
    /* Restricted iframe storage is optional. Server receipts remain authoritative. */
  }
  return null;
}
export function storePendingFriendInvitation(value: PendingFriendInvitation | null): void {
  memoryContext = value;
  try {
    if (value) sessionStorage.setItem(PENDING_INVITE_KEY, JSON.stringify(value));
    else sessionStorage.removeItem(PENDING_INVITE_KEY);
  } catch {
    storageUnavailable = true;
  }
  window.dispatchEvent(new Event(INVITE_CONTEXT_CHANGED));
}
export function subscribePendingFriendInvitation(listener: () => void): () => void {
  window.addEventListener(INVITE_CONTEXT_CHANGED, listener);
  return () => window.removeEventListener(INVITE_CONTEXT_CHANGED, listener);
}
